import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App, type PluginListenerHandle } from '@capacitor/app';

type AppStateListener = () => void;

/**
 * Hook pour les comportements liés au cycle de vie natif Capacitor.
 * Appel `onResume` quand l'app revient au premier plan (ex : rafraîchir les données).
 * Sans effet hors contexte natif.
 */
export function useNativeLifecycle(options: { onResume?: AppStateListener } = {}) {
  const onResumeRef = useRef(options.onResume);
  useEffect(() => {
    onResumeRef.current = options.onResume;
  }, [options.onResume]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let handle: PluginListenerHandle | null = null;
    let cancelled = false;

    void App.addListener('appStateChange', (state) => {
      if (!cancelled && state.isActive) {
        onResumeRef.current?.();
      }
    }).then((listener) => {
      if (cancelled) {
        void listener.remove();
      } else {
        handle = listener;
      }
    });

    return () => {
      cancelled = true;
      void handle?.remove();
    };
  }, []);
}
